import { useState, useMemo, useEffect, useCallback } from 'react';
import Groups from '@mui/icons-material/Groups';
import { Button, UserDefaultIcon } from '@practium/ui';
import { SelectionModal } from '../SelectionModal/SelectionModal';
import { SelectionCard } from '../../Cards/SelectionCard/SelectionCard';
import { LocationIcon, FarmIcon } from '../../Icons';
import type { BindingUser, PropDTO } from '../../../types';
import { fuzzyFilter } from '@practium/shared';
import gridStyles from '../SelectionModal/SelectionGrid.module.css';

export interface ClientSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (userIds: string[]) => void;
	users: BindingUser[];
	farms?: PropDTO[];
	selectedUserIds?: string[];
	showAllOption?: boolean;
	loading?: boolean;
}

const getDisplayName = (user: BindingUser): string => {
	return user.surName ? `${user.name} ${user.surName}` : user.name;
};

export const ClientSelectionModal = ({
	isOpen,
	onClose,
	onSave,
	users,
	farms = [],
	selectedUserIds = [],
	showAllOption = false,
	loading = false,
}: ClientSelectionModalProps) => {
	const initialIsAll = selectedUserIds.includes('all');

	const [search, setSearch] = useState('');
	const [isAll, setIsAll] = useState(initialIsAll);
	const [pendingIds, setPendingIds] = useState<string[]>(
		initialIsAll ? [] : selectedUserIds
	);

	const farmCountByUser = useMemo(() => {
		const counts: Record<string, number> = {};
		for (const farm of farms) {
			counts[farm.userId] = (counts[farm.userId] || 0) + 1;
		}
		return counts;
	}, [farms]);

	useEffect(() => {
		if (isOpen) {
			const allSelected = selectedUserIds.includes('all');
			setIsAll(allSelected);
			setPendingIds(allSelected ? [] : selectedUserIds);
			setSearch('');
		}
	}, [isOpen, selectedUserIds]);

	const filteredUsers = useMemo(() => {
		if (!search.trim()) return users;
		return fuzzyFilter(users, search, (u) => `${getDisplayName(u)} ${u.email} ${u.city || ''}`);
	}, [users, search]);

	const toggleUser = useCallback((userId: string) => {
		setIsAll(false);
		setPendingIds((prev) =>
			prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
		);
	}, []);

	const handleAllSelect = useCallback(() => {
		setIsAll(true);
		setPendingIds([]);
	}, []);

	const handleSave = () => {
		onSave(isAll ? ['all'] : pendingIds);
		onClose();
	};

	const currentIsAll = selectedUserIds.includes('all');
	const hasChanges =
		isAll !== currentIsAll ||
		(!isAll && !currentIsAll && pendingIds.slice().sort().join(',') !== selectedUserIds.slice().sort().join(','));

	return (
		<SelectionModal
			isOpen={isOpen}
			title="Selecione clientes para atrelar no parcelamento"
			searchPlaceholder="Buscar por nome, email ou cidade..."
			searchValue={search}
			onSearchChange={setSearch}
			onClose={onClose}
			footer={
				hasChanges ? (
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={loading}
						className={gridStyles.saveButton}
					>
						Salvar Alterações
					</Button>
				) : undefined
			}
		>
			<div className={gridStyles.grid}>
				{showAllOption && (
					<SelectionCard
						name="Todos os Clientes"
						location="Brasil"
						locationIcon={<LocationIcon />}
						farmInfo={`${farms.length} fazendas`}
						farmIcon={<FarmIcon />}
						icon={<Groups className={gridStyles.globalIcon} />}
						selected={isAll}
						onClick={handleAllSelect}
					/>
				)}
				{filteredUsers.map((user) => {
					const count = farmCountByUser[user._id] || 0;
					return (
						<SelectionCard
							key={user._id}
							name={getDisplayName(user)}
							location={user.city || user.email}
							avatar={
								user.avatar ? (
									<img
										src={user.avatar.url}
										alt={getDisplayName(user)}
										onError={(e) => {
											(e.target as HTMLImageElement).style.display = 'none';
										}}
									/>
								) : (
									<UserDefaultIcon className={gridStyles.allIcon} />
								)
							}
							locationIcon={<LocationIcon />}
							farmInfo={`${count} ${count === 1 ? 'fazenda' : 'fazendas'}`}
							farmIcon={<FarmIcon />}
							selected={pendingIds.includes(user._id)}
							onClick={() => toggleUser(user._id)}
						/>
					);
				})}
				{filteredUsers.length === 0 && !showAllOption && (
					<p className={gridStyles.empty}>Nenhum cliente encontrado</p>
				)}
			</div>
		</SelectionModal>
	);
};
